import { Injectable } from "@angular/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";

export interface FlightTimerStartedAt {
  time: number;
  ppos: [number, number] | number[] | undefined;
}

export interface LastFlightLoaded {
  flightId: string;
  flightNumber?: string;
  [key: string]: unknown;
}

// Filesystem-backed crash-recovery store — bypasses WKWebView's IDB storage
// daemon, which is the failure mode that hangs @ionic/storage reads on iOS.
@Injectable({ providedIn: "root" })
export class CrashRecoveryService {
  private static readonly DIR = "crash-recovery";
  private static readonly FLIGHT_TIMER_FILE = "flightTimerStartedAt.json";
  private static readonly LAST_FLIGHT_FILE = "lastFlightLoaded.json";
  private static readonly FUEL_ON_BOARD_FILE = "fuelOnBoard.json";

  public async getFlightTimerStartedAt(): Promise<FlightTimerStartedAt | null> {
    return this.readJson<FlightTimerStartedAt>(CrashRecoveryService.FLIGHT_TIMER_FILE);
  }

  public async setFlightTimerStartedAt(value: FlightTimerStartedAt): Promise<void> {
    await this.writeJson(CrashRecoveryService.FLIGHT_TIMER_FILE, value);
  }

  public async getLastFlightLoaded(): Promise<LastFlightLoaded | null> {
    return this.readJson<LastFlightLoaded>(CrashRecoveryService.LAST_FLIGHT_FILE);
  }

  public async setLastFlightLoaded(value: LastFlightLoaded): Promise<void> {
    await this.writeJson(CrashRecoveryService.LAST_FLIGHT_FILE, value);
  }

  public async getFuelOnBoard(): Promise<number | null> {
    const raw = await this.readJson<number>(CrashRecoveryService.FUEL_ON_BOARD_FILE);
    return typeof raw === "number" ? raw : null;
  }

  public async setFuelOnBoard(value: number): Promise<void> {
    await this.writeJson(CrashRecoveryService.FUEL_ON_BOARD_FILE, value);
  }

  private async readJson<T>(name: string): Promise<T | null> {
    try {
      const res = await Filesystem.readFile({
        directory: Directory.Data,
        path: `${CrashRecoveryService.DIR}/${name}`,
        encoding: Encoding.UTF8,
      });
      const raw = typeof res.data === "string" ? res.data : "";
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private async writeJson<T>(name: string, value: T): Promise<void> {
    // Propagate failures — silently swallowing here lets stale on-disk values
    // outlive a failed clear/update and get restored on next boot.
    await Filesystem.writeFile({
      directory: Directory.Data,
      path: `${CrashRecoveryService.DIR}/${name}`,
      data: JSON.stringify(value),
      encoding: Encoding.UTF8,
      recursive: true,
    });
  }
}
